import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Download, Upload, Columns3, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { display, openFile } from "./fields";
import RowEditor from "./RowEditor";
import ColumnsManager from "./ColumnsManager";

async function exportCsv(f) {
  const r = await fetch(`${API}/families/${f.id}/csv`, { headers: { Authorization: `Bearer ${localStorage.getItem("norea_token")}` } });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await r.blob());
  a.download = `${f.name}.csv`;
  a.click();
}

function Toolbar({ f, q, setQ, filter, setFilter, isAdmin, onAdd, onCols, onImport }) {
  const filterCols = f.columns.filter((c) => c.type === "bool" || (c.type === "choice" && c.options?.length));
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-norea-textMuted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-full pl-8 pr-3 py-1.5 text-sm border border-norea-border rounded bg-white" data-testid="family-search" />
      </div>
      {filterCols.length > 0 && (
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1.5 text-sm border border-norea-border rounded bg-white" data-testid="family-filter">
          <option value="">Tous</option>
          {filterCols.map((c) => (c.type === "bool" ? [["true", "Oui"], ["false", "Non"]] : c.options.map((o) => [o, (c.option_labels || {})[o] || o])).map(([v, l]) => <option key={`${c.id}:${v}`} value={`${c.id}:${v}`}>{c.label} : {l}</option>))}
        </select>
      )}
      <button onClick={() => exportCsv(f)} className="btn-secondary text-xs" data-testid="family-export"><Download className="w-3.5 h-3.5" /> Export CSV</button>
      <label className="btn-secondary text-xs cursor-pointer" data-testid="family-import"><Upload className="w-3.5 h-3.5" /> Import CSV<input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files[0] && onImport(e.target.files[0])} data-testid="family-import-input" /></label>
      {isAdmin && <button onClick={onCols} className="btn-secondary text-xs" data-testid="family-columns"><Columns3 className="w-3.5 h-3.5" /> Colonnes</button>}
      <button onClick={onAdd} className="btn-primary text-xs" data-testid="family-add-row"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
    </div>
  );
}

export default function FamilyTable({ familyId, families, isAdmin, onChanged }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [edit, setEdit] = useState(undefined);
  const [cols, setCols] = useState(false);
  const load = useCallback(() => api.get(`/families/${familyId}/rows`).then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible")), [familyId]);
  useEffect(() => { setData(null); setQ(""); setFilter(""); load(); }, [load]);
  const rows = useMemo(() => {
    if (!data) return [];
    const [fc, fv] = filter.split(":");
    return data.rows.filter((r) => (!q || Object.values(r.values).some((v) => JSON.stringify(v ?? "").toLowerCase().includes(q.toLowerCase())))
      && (!filter || (fv === "true" || fv === "false" ? String(!!r.values[fc]) === fv : [].concat(r.values[fc] || []).includes(fv))));
  }, [data, q, filter]);
  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const f = data.family;
  const refresh = () => { load(); onChanged(); };
  const move = async (i, dir) => {
    const ids = data.rows.map((r) => r.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    try { await api.put(`/families/${f.id}/rows-order`, { ids }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const importCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/families/${f.id}/csv`, fd);
      toast.success(`Import : ${r.data.added} ajoutée(s), ${r.data.updated} mise(s) à jour`);
      if (r.data.errors.length) toast.error(r.data.errors.slice(0, 3).join(" · "));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import impossible"); }
  };
  const canOrder = f.kind !== "materiel" && !q && !filter;
  return (
    <div data-testid={`family-table-${f.id}`}>
      {f.description && <p className="text-sm text-norea-textMuted mb-3">{f.description}</p>}
      <Toolbar f={f} q={q} setQ={setQ} filter={filter} setFilter={setFilter} isAdmin={isAdmin} onAdd={() => setEdit(null)} onCols={() => setCols(true)} onImport={importCsv} />
      <div className="overflow-x-auto border border-norea-border rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead><tr className="bg-norea-bg text-[11px] uppercase tracking-wider text-norea-textMuted">{f.columns.map((c) => <th key={c.id} className="text-left px-3 py-2 font-medium whitespace-nowrap">{c.label}</th>)}{canOrder && <th className="w-16" />}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} onClick={() => setEdit(r)} className="border-t border-norea-border/60 hover:bg-norea-surfaceHover cursor-pointer transition-colors" data-testid={`family-row-${r.id}`}>
                {f.columns.map((c) => (
                  <td key={c.id} className="px-3 py-2 max-w-[260px] truncate">
                    {c.type === "file" && r.values[c.id] ? <button onClick={(e) => { e.stopPropagation(); openFile(f.id, r.id, c.id); }} className="text-norea-primary hover:underline">{display(c, r.values[c.id])}</button> : display(c, r.values[c.id])}
                  </td>
                ))}
                {canOrder && (
                  <td className="px-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-20" data-testid={`row-up-${r.id}`}><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-1 disabled:opacity-20" data-testid={`row-down-${r.id}`}><ArrowDown className="w-3 h-3" /></button>
                  </td>
                )}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={f.columns.length + 1} className="px-3 py-8 text-center text-norea-textMuted" data-testid="family-empty">Aucune ligne</td></tr>}
          </tbody>
        </table>
      </div>
      {edit !== undefined && <RowEditor family={f} row={edit} families={families} onClose={() => setEdit(undefined)} onSaved={refresh} />}
      {cols && <ColumnsManager family={f} families={families} onClose={() => setCols(false)} onChanged={refresh} />}
    </div>
  );
}
